import { getImagePath } from "@utils/helpers/media.helper";
import { isMobile } from "react-device-detect";
import MsgComponent from "./MsgComponentQlAi";
import ReplyComponent from "./ReplyComponentQlAi";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { BASE_API_URL, REACT_APP_ASK_MORGAN_API_KEY } from "@constants/env.constants";
import { IMessage, IProjectMetaData, createSession, getProjectSettings } from "@utils/ask-morgan";
import GenericLoader from "@components/Loader/Generic";
import { IonItem, IonList, IonPopover } from "@ionic/react";
import "./QunatumloopAi.scss";
import PromptSuggestion from "./PromptSuggestionQlAi";

enum USER_LABEL_VALUE {
  USER = "user",
  CHATBOT = "chatbot"
}

const retryTimeoutMessage =
  "Sorry, we’re currently experiencing high demand. Let’s try that again for you...";
const timeoutMessage =
  "We’re currently experiencing high demand. Please retry your query shortly. Thank you for your patience!";

const errorMessagesCode = {
  timeoutErrorCode: "error_408"
};

const defaultQuestion =
  "Hello, I am AI Chatbot. Please tell me who you are so that I can provide personalized answers and convert them into better text.";

const chatApi = "/v1/quantumloop-api/chat";

const QunatumloopAi: React.FC = () => {
  const { projectId } = useParams();

  const [messages, setMessages] = useState<IMessage[]>([]);
  const [projectMetaData, setProjectMetaData] = useState<IProjectMetaData>({
    projectId: "",
    sessionId: "",
    avatar: "",
    botName: "",
    dynamicFirstQuestion: "",
    initial_prompts: []
  });
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState({ show: false, error: "" });
  const [streaming, setStreaming] = useState(false);
  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined
  });

  const chatBoxRef: any = useRef(null);
  const stopGeneratedRef = useRef({
    enableIcon: false,
    stopText: false
  });

  const addDynamicFirstQuestion = (question: string) => {
    setMessages([
      {
        text: question || defaultQuestion,
        sender: USER_LABEL_VALUE.CHATBOT
      }
    ]);
  };

  const msgJsx = {
    chatbot: (text: string, key: number, citations?: string[]) => (
      <MsgComponent
        key={key}
        logo={projectMetaData.avatar}
        botName={projectMetaData.botName}
        msg={text}
        citations={citations}
      />
    ),
    user: (text: string, key: number) => <ReplyComponent key={key} msg={text} />
  };

  const setMetaData = async (projectId: string) => {
    //* Get the project settings
    const projectSettings = await getProjectSettings(projectId);

    if (!projectSettings) return;

    const {
      data: {
        name: chatbot_name,
        suggestedPrompts,
        settings: { avatar: chatbot_avatar, initial_prompt: first_chatbot_question }
      }
    } = projectSettings;

    const initial_prompts = suggestedPrompts.map((sp) => sp.prompt);

    // const keyForStorage = `session_for_${projectId}`;
    let sessionId;
    // await getObject(keyForStorage);
    if (!sessionId) {
      // no session hence create it
      sessionId = await createSession(projectId);
      // setObject(keyForStorage, sessionId);
    }

    setProjectMetaData({
      projectId: projectId,
      sessionId: sessionId,
      avatar: chatbot_avatar ?? "",
      botName: chatbot_name,
      dynamicFirstQuestion: first_chatbot_question ?? "",
      initial_prompts
    });

    addDynamicFirstQuestion(first_chatbot_question ?? "");

    setMetadataLoading(false);
  };

  useEffect(() => {
    if (projectId) {
      setMetaData(projectId);
    }
  }, []);

  useEffect(() => {
    // Scroll down when a new message is added
    if (chatBoxRef !== null && !metadataLoading) {
      chatBoxRef.current.scrollTop = chatBoxRef?.current?.scrollHeight;
    }
  }, [messages, chatBoxRef]);

  const setNewMessageInArr = (newMsg: string, citations?: string[]) => {
    setMessages((prev) => {
      const index = prev.length - 1;
      prev[index].text = newMsg;
      citations && (prev[index].citations = citations);
      return [...prev];
    });
  };

  const doRetryForPrompt = (question: string, isRetry: boolean) => {
    setMessages((prevMessage) => {
      return !isRetry
        ? [...prevMessage]
        : [
            ...prevMessage,
            {
              text: question,
              sender: "user"
            }
          ];
    });

    // send isRetry as true because it is retry call
    sendPromptToService(question, true);
  };

  const checkForErrorInMessage = (msg: string, question: string, isRetry: boolean) => {
    if (msg === errorMessagesCode.timeoutErrorCode) {
      setNewMessageInArr(isRetry ? timeoutMessage : retryTimeoutMessage);
      // if it was not retry call then do the retry by sending prompt again
      if (!isRetry) doRetryForPrompt(question, isRetry);
      // return true to stop api call
      return true;
    }
  };

  // this contain streaming
  const sendPromptToService = (question: string, isRetry = false) => {
    fetch(`${BASE_API_URL}${chatApi}/${projectMetaData.sessionId}`, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
        "x-api-key": REACT_APP_ASK_MORGAN_API_KEY
      },
      body: JSON.stringify({
        question: question
      }) // body data type must match "Content-Type" header
    }).then((res: any) => {
      const reader = res.body.getReader();
      setLoading(false);
      setStreaming(true);
      let str = "";
      let sourceLinks: string[] | undefined = undefined;
      const read = () => {
        // read the data
        reader.read().then(({ done, value: valueBuffer }: any) => {
          const decoder = new TextDecoder();
          let value = decoder.decode(valueBuffer).toString();
          console.log("Value->", value);
          if (
            checkForErrorInMessage(value, question, isRetry) ||
            done ||
            stopGeneratedRef.current.stopText ||
            value === "TOKEN_END"
          ) {
            stopGeneratedRef.current = {
              enableIcon: false,
              stopText: false
            };
            setStreaming(false);
            return;
          } else if (value.startsWith("SOURCE_START")) {
            const sourcesStr = value.replace("SOURCE_START", "").replace("SOURCE_END", "");
            sourceLinks = sourcesStr.split("<SOURCE_BR>");
            value = "";
          }

          str += value;
          setNewMessageInArr(str, sourceLinks);
          read();
        });
      };

      setMessages((prev) => {
        return [...prev, { text: "", sender: "chatbot" }];
      });

      read();
    });
    //   fetch end
  };

  const handleSendMessage = async (suggestedPrompt?: string) => {
    if (!suggestedPrompt && (userInput === "" || userInput === null || userInput === undefined)) {
      setInputError({
        show: true,
        error: "Message cannot be empty"
      });
      return;
    }
    if (streaming) {
      setInputError({
        show: true,
        error:
          "Cannot send message while the bot is answering, Please wait or click 'Stop Generating' and try again"
      });
      return;
    }
    setLoading(true);

    // start : this thing will make sure before sending message stopGeneration is false
    stopGeneratedRef.current = {
      enableIcon: true,
      stopText: false
    };

    // end : this thing will make sure before sending message stopGeneration is false
    const newMessage = suggestedPrompt || userInput;
    setMessages([...messages, { text: newMessage, sender: "user" }]);
    setUserInput("");
    sendPromptToService(newMessage);
    setInputError({
      show: false,
      error: ""
    });
  };

  const clearConversation = () => {
    if (!stopGeneratedRef.current.enableIcon) setMessages([]);
    addDynamicFirstQuestion(projectMetaData.dynamicFirstQuestion);
  };

  const stopGeneration = () => {
    stopGeneratedRef.current = {
      enableIcon: false,
      stopText: true
    };
    setStreaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendMessage();
      return;
    }
    return;
  };

  return (
    <div className="chatbot_container">
      {metadataLoading ? (
        <div className="chatbot-preloader">
          <GenericLoader />
        </div>
      ) : (
        <>
          {/* conditional class "border_bottom" */}
          <div className="header-wrapper">
            <div className="chatbot-header">
              <div className="header-col"></div>
              <div className="header-col header_title">{projectMetaData.botName} Ai assistant </div>
              <div className="header-col">
                {/* {!isMobile && <div className="header-language">English</div>} */}
                {isMobile && (
                  <>
                    <IonPopover
                      cssClass="header-language"
                      event={popoverState.event}
                      isOpen={popoverState.showPopover}
                      onDidDismiss={() => setShowPopover({ showPopover: false, event: undefined })}
                    >
                      <IonList>
                        <IonItem
                          className="close-button-mobile"
                          lines="none"
                          detail={false}
                          onClick={() => {
                            clearConversation();
                            setShowPopover({ showPopover: false, event: undefined });
                          }}
                        >
                          <div className="close-button-items">
                            <span>Clear all chat</span>
                            <img
                              onClick={() => {
                                return;
                              }}
                              src={getImagePath(`svg/bin_icon.svg`)}
                              alt="send"
                            />
                          </div>
                        </IonItem>
                      </IonList>
                    </IonPopover>
                    <button
                      className="header-button"
                      onClick={(e: any) => {
                        e.persist();
                        setShowPopover({ showPopover: true, event: e });
                      }}
                    >
                      <img
                        onClick={() => {
                          return;
                        }}
                        src={getImagePath(`svg/3dots_circle.svg`)}
                        alt="send"
                      />
                    </button>
                  </>
                )}
              </div>
            </div>
            {isMobile && <div className="header-powered_text">Powered by QuantumLoopAi</div>}
          </div>
          <div ref={chatBoxRef} className="chatbot-content">
            {messages.map((message, index) => {
              if (message.sender === "chatbot")
                return msgJsx.chatbot(message.text, index, message.citations);
              else return msgJsx.user(message.text, index);
            })}
            {loading ? (
              <div className="msg-container">
                <div className="msg-avatar">
                  <img
                    alt="image"
                    src={projectMetaData.avatar}
                    loading="lazy"
                    className="msg-image"
                  />
                </div>
                <div className="msg-content_wrapper">
                  <div className="company_name">{projectMetaData.botName}</div>
                  <div className="chat_prompt"></div>
                  <div className="msg_loading">
                    <img src={getImagePath(`svg/loading_grey.svg`)} alt="send" />
                  </div>
                </div>
              </div>
            ) : null}
            {streaming && !loading ? (
              <div className="msg-container">
                <div className="msg-avatar"></div>
                <div className="msg-content_wrapper">
                  <div className="company_name"></div>
                  <div className="chat_prompt"></div>
                  <div
                    className={`msg_loading ${streaming && !loading ? "modify-margin-top" : null}`}
                  >
                    <img src={getImagePath(`svg/loading_grey.svg`)} alt="send" />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          {streaming ? (
            isMobile ? null : (
              <button className="stop-streaming-btn" onClick={stopGeneration}>
                <span className="stop-streaming-btn-icon">
                  <img src={getImagePath(`svg/stop.svg`)} alt="send" />
                </span>
                Stop Generating
              </button>
            )
          ) : null}
          {!streaming ? (
            <PromptSuggestion
              prompts={projectMetaData.initial_prompts}
              handleSendMessageCallback={handleSendMessage}
            />
          ) : null}
          <div className="chatbot-footer">
            <div className="footer-input_container">
              <textarea
                onChange={(e) => {
                  setInputError({
                    show: false,
                    error: ""
                  });
                  setUserInput(e.target.value);
                }}
                value={userInput}
                className="footer-chat_input"
                placeholder={loading ? "Thinking..." : "Ask me anything..."}
                onKeyDown={(e) => {
                  handleKeyDown(e);
                }}
              ></textarea>
              {/* "active" class should be conditional with send button  */}
              {!isMobile ? (
                <button
                  className={`send_btn ${userInput.length ? "active" : null}`}
                  onClick={() => handleSendMessage()}
                  disabled={streaming || !userInput.length}
                >
                  <img src={getImagePath(`svg/send_msg_btn.svg`)} alt="send" />
                </button>
              ) : null}
              {isMobile ? (
                streaming ? (
                  <button className="stop_btn" onClick={stopGeneration}>
                    <img src={getImagePath(`svg/stop-stream.svg`)} alt="send" />
                  </button>
                ) : (
                  <button
                    className={`send_btn ${userInput.length ? "active" : null}`}
                    onClick={() => handleSendMessage()}
                    disabled={streaming || !userInput.length}
                  >
                    <img src={getImagePath(`svg/send_msg_btn.svg`)} alt="send" />
                  </button>
                )
              ) : null}
              {!isMobile &&
                (loading ? null : (
                  <button className="delete_btn" onClick={clearConversation}>
                    <img src={getImagePath(`svg/trash_grey.svg`)} alt="send" />
                  </button>
                ))}
            </div>
            {inputError.show ? (
              <span className="chatbot-textarea-error" style={{ color: "red" }}>
                {inputError.error}
              </span>
            ) : null}
            {!isMobile && <div className="footer-powered_text">Powered by QuantumLoopAi</div>}
          </div>
        </>
      )}
    </div>
  );
};

export default QunatumloopAi;
